import { useEffect } from "react"
import { enqueueSnackbar } from "notistack"
import { faFileArrowDown, faPrint } from "@fortawesome/pro-light-svg-icons"

import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { useDateTime } from "../../../../localization/useDateTime"
import { ActionBarItem, FloatingActionBar } from "../../../../../shared/components/table/FloatingActionBar"
import {
    LabelPrinterDialogVariant,
    LabelPrinterExtensionDialog,
} from "../../../../../shared/components/dialogs/LabelPrinterExtensionDialog"
import { PrintStatus, useLabelPrinter } from "../../home/hooks/useLabelPrinter"
import { GuestData } from "../../external/brotherPrinter"
import { GuestInfo, GuestInfoType } from "../guestListTypes"

type GuestsFloatingActionBarProps = Readonly<{
    selectedRows: GuestInfo[]
    onClearSelected: () => void
    onExport: () => void
}>

// Extracted from Guests so the frequent `currentLabelIndex` updates during printing
// only re-render this small bar, not the whole (heavy) guests table.
export function GuestsFloatingActionBar({ selectedRows, onClearSelected, onExport }: GuestsFloatingActionBarProps) {
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()

    const {
        printMultipleGuestLabels,
        printStatus,
        printErrorMessage,
        isLoading,
        hasSuccess,
        isInstalledError,
        isPrinterNotFound,
        clearState,
    } = useLabelPrinter()

    // Fire a single toast per terminal print status (each print starts from PRINTING,
    // so this runs once per attempt instead of once per failed guest).
    // NOT_INSTALLED / PRINTER_NOT_FOUND are handled by LabelPrinterExtensionDialog below.
    useEffect(() => {
        if (printStatus === PrintStatus.SUCCESS) {
            enqueueSnackbar({
                variant: "success",
                message: l10n.getString("label-printer-success-labels-printed"),
            })
            const timeout = setTimeout(() => clearState(), 2000)
            return () => clearTimeout(timeout)
        }

        if (printStatus === PrintStatus.ERROR) {
            enqueueSnackbar({
                variant: "error",
                message: printErrorMessage || l10n.getString("label-printer-error-failed-guest-label"),
            })
        }

        return undefined
    }, [printStatus])

    const handlePrintLabels = async () => {
        if (!selectedRows.length) {
            return
        }

        const labelsToPrint: GuestData[] = selectedRows.map((guest) => {
            const guestCard = guest.guestCardDetails?.guestCards?.[0]

            return {
                visitorName: guest.type === GuestInfoType.SINGLE ? guest.attendee.name : "",
                hostName: guest.host.displayName,
                visitorCompany: guest.attendee.company,
                checkOutCode: guestCard?.checkOutCode,
                validTo: guestCard?.validTo
                    ? dateTimeFactory.formatDate(dateTimeFactory.fromISO(guestCard.validTo))
                    : undefined,
            }
        })

        await printMultipleGuestLabels(labelsToPrint)
    }

    return (
        <>
            <FloatingActionBar selectedItemsLength={selectedRows.length} handleCloseActionBar={onClearSelected}>
                <LocalizedStrict
                    id="service-center-reports-dashboard-guests-table-floating-action-bar-guest-card"
                    attrs={{ title: true }}
                >
                    <ActionBarItem
                        icon={faPrint}
                        title="Guest card"
                        isLoading={isLoading}
                        isSuccess={hasSuccess}
                        onClick={() => handlePrintLabels()}
                    />
                </LocalizedStrict>

                <LocalizedStrict id="company-meeting-service-table-floating-action-bar-export" attrs={{ title: true }}>
                    <ActionBarItem icon={faFileArrowDown} title="Export" onClick={onExport} />
                </LocalizedStrict>
            </FloatingActionBar>
            <LabelPrinterExtensionDialog
                open={isInstalledError}
                onClose={clearState}
                variant={LabelPrinterDialogVariant.NOT_INSTALLED}
            />
            <LabelPrinterExtensionDialog
                open={isPrinterNotFound}
                onClose={clearState}
                variant={LabelPrinterDialogVariant.PRINTER_NOT_FOUND}
            />
        </>
    )
}

